import React from 'react';
import HealthSpendingChart from './charts/HealthSpendingChart';
import FederalDebtChart from './charts/FederalDebtChart';
import HouseholdBurdenChart from './charts/HouseholdBurdenChart';
import DrugSpendingChart from './charts/DrugSpendingChart';
import MedSchoolDebtChart from './charts/MedSchoolDebtChart';
import MedicalDebtChart from './charts/MedicalDebtChart';
import MacroImpactChart from './charts/MacroImpactChart';
import CostFundingChart from './charts/CostFundingChart';
import CostBreakdownTable from './CostBreakdownTable';
import LtssInfoCard from './LtssInfoCard';
import DataSourcesDisclaimer from './DataSourcesDisclaimer';
import DataExportPanel from './DataExportPanel';
import SuccessGauge from './SuccessGauge';
import StateStimulusChart from './charts/StateStimulusChart';
import PayVsGetChart from './charts/PayVsGetChart';
import { PARAMETER_REGISTRY, listPolicyParameters } from '../model/parameterRegistry.js';
import '../../styles/pages/outcome-dashboard.css';

// Helper to compute exact parameter deltas to close the deficit
function calculateGapClosure(strategy, deficit, currentConfig) {
    if (deficit <= 0) return null;

    // SCALARS (Based on 2027 Projections from income_base.json)
    // Wage Base ~$11T -> 1% = $110B
    // Non-Wage Base ~$4.7T -> 1% = $47B (Wealth Tax / Surtax proxy)
    const REVENUE_PER_1PCT_PAYROLL = 110e9;
    const REVENUE_PER_1PCT_WEALTH = 47e9;

    // Limits
    const MAX_SEED = 2000e9; // $2T Cap

    let changes = {};
    let description = [];

    const deficitToCover = deficit;

    switch (strategy) {
        case 'balanced':
            // 40% Payroll (Emp+Ind), 30% Seed/Wealth, 30% Efficiency
            const payrollTarget = deficitToCover * 0.4;
            const seedTarget = deficitToCover * 0.3;
            // Efficiency covers the rest implicitly or we set it high

            // Payroll (Split Emp/Ind)
            const payrollHike = (payrollTarget / REVENUE_PER_1PCT_PAYROLL) / 100 / 2;
            changes.employerRate = (currentConfig.employerRate || 0.08) + payrollHike;
            changes.baseIndividualRate = (currentConfig.baseIndividualRate || 0.035) + payrollHike;
            if (payrollHike > 0.0001) description.push(`+${(payrollHike * 100).toFixed(2)}% Payroll Tax`);

            // Seed 
            changes.initialTrustFundSeed = Math.min(MAX_SEED, (currentConfig.initialTrustFundSeed || 0) + seedTarget);
            if (seedTarget > 1e9) description.push(`+$${(seedTarget / 1e9).toFixed(0)}B Seed Fund`);

            // Spend Cuts (Admin Eff)
            changes.adminEfficiencyFloor = 0.015; // Hard set to 1.5%
            description.push("Max Admin Efficiency");
            break;

        case 'labor':
            // Protect Labor. 60% Wealth, 40% Seed
            const wealthTarget = deficitToCover * 0.6;
            const laborSeedTarget = deficitToCover * 0.4;

            const wealthHike = (wealthTarget / REVENUE_PER_1PCT_WEALTH) / 100;
            // Assuming we map wealth tax to surtax or a specific param. 
            // If wealthTaxRate exists in config, use it. Else pump surtaxes? 
            // Config has `wealthTaxRate` based on plan.
            changes.wealthTaxRate = (currentConfig.wealthTaxRate || 0) + wealthHike;
            changes.initialTrustFundSeed = Math.min(MAX_SEED, (currentConfig.initialTrustFundSeed || 0) + laborSeedTarget);

            description.push(`+${(wealthHike * 100).toFixed(2)}% Wealth Tax`);
            description.push(`+$${(laborSeedTarget / 1e9).toFixed(0)}B Seed Fund`);
            break;

        case 'infra':
            // Protect Providers. 100% Tax (70% Payroll, 30% Wealth)
            const infraPayrollTarget = deficitToCover * 0.7;
            const infraWealthTarget = deficitToCover * 0.3;

            const infraPayrollHike = (infraPayrollTarget / REVENUE_PER_1PCT_PAYROLL) / 100 / 2;
            changes.employerRate = (currentConfig.employerRate || 0.08) + infraPayrollHike;
            changes.baseIndividualRate = (currentConfig.baseIndividualRate || 0.035) + infraPayrollHike;

            const infraWealthHike = (infraWealthTarget / REVENUE_PER_1PCT_WEALTH) / 100;
            changes.wealthTaxRate = (currentConfig.wealthTaxRate || 0) + infraWealthHike;

            description.push(`+${(infraPayrollHike * 100).toFixed(2)}% Payroll Tax`);
            description.push(`+${(infraWealthHike * 100).toFixed(2)}% Wealth Tax`);
            break;

        case 'tax':
            // Min Taxes. 80% Seed, 20% Caps
            const taxSeedTarget = deficitToCover * 0.8;
            changes.initialTrustFundSeed = Math.min(MAX_SEED, (currentConfig.initialTrustFundSeed || 0) + taxSeedTarget);

            changes.capitalGrantsAnnual = (currentConfig.capitalGrantsAnnual || 100e9) * 0.5; // 50% cut
            changes.debtRedemptionBudget = 0;

            description.push(`+$${(taxSeedTarget / 1e9).toFixed(0)}B Seed Fund`);
            description.push("Cut Grants & Debt Pay");
            break;

        case 'growth':
            // Growth Optimized. 100% Individual Tax
            const growthTarget = deficitToCover;
            const indHike = (growthTarget / REVENUE_PER_1PCT_PAYROLL) / 100; // Entirely on employee side

            changes.baseIndividualRate = (currentConfig.baseIndividualRate || 0.035) + indHike;
            description.push(`+${(indHike * 100).toFixed(2)}% Indiv. Tax`);
            break;
    }

    return { changes: { ...currentConfig, ...changes }, description };
}

/**
 * Outcome Dashboard Component
 * Displays all visualization charts and summary metrics
 */
export default function OutcomeDashboard({
    results,
    stateResults,
    selectedState,
    config,
    baselineData,
    onUpdateConfig,
    rigorData,
    uncertaintyResult
}) {
    if (!results || !results.scenarioResults || results.scenarioResults.length === 0) {
        return (
            <div className="outcome-dashboard">
                <div className="loading-state">
                    <p>Loading scenario results...</p>
                </div>
            </div>
        );
    }

    if (results.error) {
        return (
            <div className="outcome-dashboard">
                <div className="error-state">
                    <h3>Error Running Scenario</h3>
                    <p>{results.error}</p>
                </div>
            </div>
        );
    }

    const { baselineResults, scenarioResults, householdBurden, householdSummary, summary, macroImpacts } = results;

    // Use Scenario Results (With Events) as the primary data for tables/summaries
    const fullResults = scenarioResults || [];

    // Slice data to match the selected Simulation Period (slider)
    // Capped at 21 to match authoritative data horizon
    const displayCount = Math.min(config.simulationYears || 21, 21);
    const yearlyResults = fullResults.slice(0, displayCount); // "yearlyResults" passed to charts is now SLICED
    const baselineSliced = baselineResults ? baselineResults.slice(0, displayCount) : [];

    // Filter data to start from 2026 as requested (though usually startYear is 2027)
    // If we rely on slice(0, displayCount), it starts from index 0.
    const displayResults = yearlyResults;

    // Also slice macro impacts
    const displayMacro = macroImpacts ? macroImpacts.slice(0, displayCount) : [];

    // Get final year GDP impact for summary
    const finalMacro = displayMacro.length > 0 ? displayMacro[displayMacro.length - 1] : null;
    const finalGDPImpact = finalMacro ? (finalMacro.gdpImpact * 100).toFixed(1) : '0.0';

    // Check if SAFECARE is revenue-neutral
    const firstYearRevenue = yearlyResults[0]?.totalRevenue || 0;
    const firstYearSpending = yearlyResults[0]?.totalSpending || 0;
    const rawGap = firstYearSpending - firstYearRevenue;

    // Section 207 allows general fund transfers up to 20% of outlays in first 2 years
    const permittedGeneralFundTransfer = firstYearSpending * 0.20;
    const gapAfterGeneralFund = rawGap - permittedGeneralFundTransfer;

    const hasDeficit = gapAfterGeneralFund > 0;
    const deficitAmount = Math.max(0, gapAfterGeneralFund);

    // ... existing imports ...

    // ... existing logic ...

    // Check if any events are active to decide whether to show the yellow line
    const showEventsLine = yearlyResults.some(y => y.activeEvents && y.activeEvents.length > 0);

    // GENERATE PRINTABLE REPORT (Lifted from DataExportPanel)
    const generatePrintableReport = () => {
        const timestamp = new Date().toLocaleString();

        // Use displayed results
        const activeData = yearlyResults;
        if (!activeData || activeData.length === 0) return;

        // Helper to format numbers with commas
        const fmt = (val, decimals = 1) => val.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

        // --- 1. Calculate Executive Summary Metrics ---
        const firstYear = activeData[0];
        const lastYear = activeData[activeData.length - 1];

        const totalRevenue = activeData.reduce((sum, d) => sum + d.totalRevenue, 0);
        const totalCost = activeData.reduce((sum, d) => sum + d.totalSpending, 0);
        const avgSurplus = (totalRevenue - totalCost) / activeData.length;

        const startingHealthShare = firstYear.baselineHealthShareGDP;
        const endingHealthShare = lastYear.safecareHealthShareGDP;
        const healthShareReduction = ((startingHealthShare - endingHealthShare) * 100).toFixed(1);

        const debtReduction = ((lastYear.baselineDebtToGDP - lastYear.safecareDebtToGDP) * 100).toFixed(1);
        const medicalDebtEliminated = ((activeData[0].medicalDebtBaseline - lastYear.medicalDebtSafecare) / 1e9).toFixed(1); // Keep as string for display logic below if needed, but we'll re-format

        // --- 2. Prepare Policy Configuration List ---
        const policyParams = listPolicyParameters(config, { onlyChanged: false }).map(p => {
            const regItem = PARAMETER_REGISTRY.find(r => r.key === p.key);
            return {
                label: regItem ? regItem.label : p.key,
                value: typeof p.value === 'number' && p.value < 1 && p.value > -1 ? (p.value * 100).toFixed(1) + '%' : p.value,
                group: regItem ? regItem.group : 'Other'
            };
        });

        const groupedPolicies = policyParams.reduce((acc, item) => {
            if (!acc[item.group]) acc[item.group] = [];
            acc[item.group].push(item);
            return acc;
        }, {});

        // --- 3. Construct HTML ---
        const reportHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>SAFECARE+ Simulation Report</title>
                <style>
                    body { font-family: 'Lato', 'Segoe UI', sans-serif; color: #1e293b; padding: 40px; max-width: 1000px; margin: 0 auto; line-height: 1.5; }
                    h1 { font-size: 28px; color: #0f172a; margin-bottom: 5px; border-bottom: 4px solid #2563eb; padding-bottom: 15px; }
                    h2 { font-size: 20px; color: #1e293b; margin-top: 40px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; font-weight: 700; }
                    h3 { font-size: 14px; color: #64748b; margin-top: 20px; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 700; }
                    .header-meta { font-size: 12px; color: #64748b; margin-bottom: 40px; display: flex; justify-content: space-between; font-weight: 500; }
                    
                    /* KPI Cards */
                    .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 30px; }
                    .kpi-card { border: 1px solid #e2e8f0; padding: 20px; border-radius: 12px; background: #ffffff; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); }
                    .kpi-label { font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 700; letter-spacing: 0.5px; }
                    .kpi-value { font-size: 24px; font-weight: 800; color: #0f172a; margin-top: 8px; }
                    .positive { color: #059669; }
                    .negative { color: #dc2626; }

                    /* Data Table */
                    table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 20px; }
                    th { text-align: right; padding: 12px 10px; border-bottom: 2px solid #cbd5e1; background: #f8fafc; color: #475569; font-weight: 700; }
                    th:first-child { text-align: left; }
                    td { text-align: right; padding: 10px; border-bottom: 1px solid #f1f5f9; }
                    td:first-child { text-align: left; font-weight: 600; color: #334155; }
                    tr:nth-child(even) { background: #f8fafc; }

                    /* Config Grid */
                    .config-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; font-size: 13px; }
                    .config-group { margin-bottom: 25px; }
                    .config-item { display: flex; justify-content: space-between; border-bottom: 1px solid #f1f5f9; padding: 8px 0; }
                    .config-key { color: #64748b; font-weight: 500; }
                    .config-val { font-weight: 600; color: #0f172a; }

                    /* State Section */
                    .state-box { background: #eff6ff; border-left: 5px solid #2563eb; padding: 20px; border-radius: 0 8px 8px 0; margin: 30px 0; }
                    .state-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-top: 15px; }
                    .state-metric { text-align: center; }
                    .state-label { font-size: 10px; color: #3b82f6; text-transform: uppercase; font-weight: 700; }
                    .state-value { font-size: 18px; font-weight: 800; color: #1e3a8a; }

                    /* Assumptions */
                    .assumptions { font-size: 11px; color: #94a3b8; margin-top: 60px; border-top: 1px solid #e2e8f0; padding-top: 20px; font-style: italic; }
                    /* Methods appendix (separate print page) */
                    .appendix-page { page-break-before: always; break-before: page; margin-top: 10px; }
                    .appendix-intro { background: #f8fafc; border: 1px solid #e2e8f0; border-left: 4px solid #2563eb; border-radius: 10px; padding: 14px 16px; font-size: 12px; color: #334155; margin: 12px 0 16px 0; }
                    .appendix-section { margin-bottom: 24px; }
                    .appendix-section h3 { margin-top: 0; color: #1e293b; font-size: 13px; letter-spacing: 0.08em; }
                    .appendix-section p { font-size: 12px; color: #334155; margin: 8px 0; }
                    .appendix-list { margin: 8px 0; padding-left: 18px; font-size: 12px; color: #334155; }
                    .appendix-list li { margin: 6px 0; }
                    .method-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
                    .method-card { border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; background: #fff; }
                    .equation { font-family: 'JetBrains Mono', 'Consolas', monospace; font-size: 11px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px; color: #0f172a; display: block; margin: 8px 0; }
                    .source-table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
                    .source-table th { text-align: left; padding: 8px; border-bottom: 1px solid #cbd5e1; background: #f8fafc; }
                    .source-table td { text-align: left; padding: 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
                    .critic-note { background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; padding: 10px 12px; font-size: 11px; color: #78350f; margin-top: 8px; }
                    
                    @media print {
                        body { padding: 0; }
                        button { display: none; }
                        .no-break { page-break-inside: avoid; }
                        .kpi-card { box-shadow: none; border: 1px solid #e2e8f0; }
                    }
                </style>
            </head>
            <body>
                <div class="header-meta">
                    <span>Generated by SAFECARE+ Reform Engine</span>
                    <span>${timestamp}</span>
                </div>
                <h1>Simulation Outcome Report</h1>
                
                <h2>National Executive Summary (${activeData[0].year} - ${activeData[activeData.length - 1].year})</h2>
                <div class="kpi-grid">
                    <div class="kpi-card">
                        <div class="kpi-label">Avg Annual Surplus</div>
                        <div class="kpi-value ${(avgSurplus >= 0 ? 'positive' : 'negative')}">
                            ${(avgSurplus >= 0 ? '+' : '')}$${fmt(avgSurplus / 1e9)}B
                        </div>
                    </div>
                    <div class="kpi-card">
                        <div class="kpi-label">Debt Reduction</div>
                        <div class="kpi-value positive">-${debtReduction}% GDP</div>
                    </div>
                    <div class="kpi-card">
                        <div class="kpi-label">Health Share GDP</div>
                        <div class="kpi-value positive">-${healthShareReduction}%</div>
                    </div>
                    <div class="kpi-card">
                        <div class="kpi-label">Med Debt Eliminated</div>
                        <div class="kpi-value positive">$${fmt(parseFloat(medicalDebtEliminated))}B</div>
                    </div>
                </div>

                ${selectedState && stateResults ? `
                    <div class="state-box">
                        <div class="kpi-label" style="color: #1e40af; font-size: 14px;">State Focus: ${selectedState}</div>
                        <div class="state-grid">
                            <div class="state-metric">
                                <div class="state-label">Net Fiscal Gain</div>
                                <div class="state-value">$${fmt(stateResults.summary.totalStimulus / 1e9)}B</div>
                            </div>
                            <div class="state-metric">
                                <div class="state-label">Fiscal Benefit Ratio</div>
                                <div class="state-value">${stateResults.summary.avgRatio.toFixed(2)}x</div>
                            </div>
                            <div class="state-metric">
                                <div class="state-label">Direct Benefits</div>
                                <div class="state-value">$${fmt(stateResults.summary.totalGet / 1e9)}B</div>
                            </div>
                            <div class="state-metric">
                                <div class="state-label">Federal Tax Cost</div>
                                <div class="state-value">$${fmt(stateResults.summary.totalPay / 1e9)}B</div>
                            </div>
                        </div>
                    </div>
                ` : ''}

                <h2>Financial Projections</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Year</th>
                            <th>Revenue</th>
                            <th>Spending</th>
                            <th>Surplus</th>
                            <th>Trust Fund</th>
                            <th>Reserve %</th>
                            <th>Fed Debt</th>
                            <th>Health % GDP</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${activeData.map(d => `
                            <tr>
                                <td>${d.year}</td>
                                <td>$${fmt(d.totalRevenue / 1e9)}B</td>
                                <td>$${fmt(d.totalSpending / 1e9)}B</td>
                                <td class="${(d.totalRevenue - d.totalSpending) >= 0 ? 'positive' : 'negative'}">
                                    ${(d.totalRevenue - d.totalSpending) >= 0 ? '+' : ''}$${fmt((d.totalRevenue - d.totalSpending) / 1e9)}B
                                </td>
                                <td>$${fmt(d.trustFundBalance / 1e9)}B</td>
                                <td>${fmt(d.trustFundReserveRatio * 100)}%</td>
                                <td>${fmt(d.safecareDebtToGDP * 100, 0)}%</td>
                                <td>${fmt(d.safecareHealthShareGDP * 100)}%</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>

                <div class="no-break">
                    <h2>Policy Configuration</h2>
                    <div class="config-grid">
                        ${Object.entries(groupedPolicies).map(([group, items]) => `
                            <div class="config-group">
                                <h3>${group}</h3>
                                ${items.map(i => `
                                    <div class="config-item">
                                        <span class="config-key">${i.label}</span>
                                        <span class="config-val">${i.value}</span>
                                    </div>
                                `).join('')}
                            </div>
                        `).join('')}
                    </div>
                </div>

                <div class="assumptions">
                    <strong>Model Disclaimer:</strong> 
                    Projections are based on microsimulation data from IRS SOI, US Census, and NHE historicals. 
                    Macroeconomic impacts include dynamic GDP feedback loops. 
                    State-level spending figures assume full participation in SAFECARE+ administrative protocols.
                </div>

                <div class="appendix-page">
                    <h2>Appendix A — Model Logic, Assumptions, and Data Sources</h2>
                    <div class="appendix-intro">
                        This appendix is intentionally detailed for technical reviewers and critics. It documents the simulator pipeline, parameter assumptions, dynamic interactions, and source provenance for key variables.
                    </div>

                    <div class="appendix-section">
                        <h3>1) MODEL ARCHITECTURE (PIPELINE ORDER)</h3>
                        <ol class="appendix-list">
                            <li><strong>Inputs:</strong> Policy configuration, event toggles, baseline year series, and optional structural scenario overrides.</li>
                            <li><strong>Spending module:</strong> Projects SAFECARE spending path by year, including enrollment ramp, administrative efficiency path, negotiated rates/caps, and targeted intervention effects.</li>
                            <li><strong>Revenue module:</strong> Computes annual trust-fund inflows from payroll/individual contributions, surtaxes, wealth-linked contributions (when enabled), and compliance effects.</li>
                            <li><strong>Trust-fund / fiscal module:</strong> Updates annual trust-fund balance, reserve ratio, and federal debt interaction under baseline vs reform tracks.</li>
                            <li><strong>Debt module:</strong> Applies medical debt redemption and med-school/workforce debt pathways according to configured annual budgets and timing.</li>
                            <li><strong>Household module:</strong> Converts aggregate effects into household burden/savings distribution estimates.</li>
                            <li><strong>Macro module:</strong> Applies GDP multipliers and growth feedback assumptions tied to household relief, price stabilization, and labor effects.</li>
                            <li><strong>State incidence module:</strong> Produces Pay-vs-Get and net stimulus outputs for selected state views.</li>
                        </ol>
                    </div>

                    <div class="appendix-section">
                        <h3>2) KEY EQUATIONS AND ACCOUNTING IDENTITIES</h3>
                        <span class="equation">Annual Net Fiscal Balance = Total Revenue - Total Spending</span>
                        <span class="equation">Trust Fund(t) = Trust Fund(t-1) + Revenue(t) - Spending(t) + Seed/Transfers(t)</span>
                        <span class="equation">Debt-to-GDP Reform Gap = Baseline Debt/GDP - SAFECARE Debt/GDP</span>
                        <span class="equation">State Net Stimulus = (Federal Benefits + Household Savings) - State-Contributed Federal Taxes</span>
                        <p>All dashboard summaries are derived from year-level series, not single-year snapshots. Totals and averages in this report use the same annual vectors used by the interactive charts.</p>
                    </div>

                    <div class="appendix-section method-grid">
                        <div class="method-card">
                            <h3>3) CORE ASSUMPTION BLOCKS</h3>
                            <ul class="appendix-list">
                                <li>Enrollment and benefit transitions follow a multi-year ramp rather than instantaneous full conversion.</li>
                                <li>Administrative savings are phased with a floor/ceiling structure and are not assumed to appear fully in year one.</li>
                                <li>Drug and rate controls have delayed and compounding effects; short-run and long-run savings differ.</li>
                                <li>Fraud/compliance effects are bounded and parameterized (not unconstrained static gains).</li>
                                <li>Labor pass-through and wage effects are modeled as partial, time-dependent propagation.</li>
                            </ul>
                        </div>
                        <div class="method-card">
                            <h3>4) STRESS / SCENARIO ASSUMPTIONS</h3>
                            <ul class="appendix-list">
                                <li>Shock events modify selected parameter blocks (cost growth, financing, implementation frictions) by year.</li>
                                <li>Scenario presets bundle config + event overrides to represent coherent policy stances.</li>
                                <li>Monte Carlo uncertainty, where run, perturbs bounded assumptions and reports percentile bands.</li>
                                <li>Structural overrides are tracked separately from user slider edits for auditability.</li>
                            </ul>
                        </div>
                    </div>

                    <div class="appendix-section">
                        <h3>5) DATA SOURCES AND PROVENANCE (HIGH-LEVEL)</h3>
                        <table class="source-table">
                            <thead>
                                <tr>
                                    <th>Domain</th>
                                    <th>Primary Source Families</th>
                                    <th>How Used in Model</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>National Health Expenditure Baseline</td>
                                    <td>CMS/NHE historical and projection series</td>
                                    <td>Anchors baseline spending path and category growth trajectories.</td>
                                </tr>
                                <tr>
                                    <td>Federal Debt / Trust Context</td>
                                    <td>Treasury / CBO-style debt and trust-fund baselines</td>
                                    <td>Reference path for baseline debt-to-GDP and reserve comparisons.</td>
                                </tr>
                                <tr>
                                    <td>Household / Income Distribution</td>
                                    <td>IRS SOI + Census/PUMS-aligned income distributions</td>
                                    <td>Maps financing and savings effects into decile/household burden outputs.</td>
                                </tr>
                                <tr>
                                    <td>Medical Debt / Workforce Debt</td>
                                    <td>Consumer-finance + education debt datasets</td>
                                    <td>Initial conditions for debt redemption and workforce debt trajectories.</td>
                                </tr>
                                <tr>
                                    <td>State Allocation Inputs</td>
                                    <td>Population, income base, and spending incidence by state</td>
                                    <td>Computes state Pay-vs-Get and net stimulus totals.</td>
                                </tr>
                                <tr>
                                    <td>Calibration Benchmarks</td>
                                    <td>Published historical anchors (e.g., Medicare admin, tax-gap/fraud comparators, OECD price references)</td>
                                    <td>Used for reasonableness checks and calibration diagnostics, not direct one-to-one forecasting.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="appendix-section">
                        <h3>6) CRITIC-CHECKLIST: WHAT TO VERIFY</h3>
                        <ol class="appendix-list">
                            <li>Reproduce baseline year values before applying reforms (sanity check on source alignment).</li>
                            <li>Toggle one assumption at a time and verify directional consistency across spending, revenue, and debt charts.</li>
                            <li>Compare short-horizon vs long-horizon effects to identify phase-in dependency.</li>
                            <li>Validate that trust-fund reserve behavior matches arithmetic identity in section 2.</li>
                            <li>Inspect sensitivity under adverse scenarios and/or uncertainty runs for robustness.</li>
                            <li>Review parameter registry labels and defaults against legislative text assumptions.</li>
                        </ol>
                        <div class="critic-note">
                            Important: this simulator is a policy-analysis tool, not an oracle. It is designed for transparent scenario comparison, assumption stress-testing, and explicit accounting of trade-offs under configurable rules.
                        </div>
                    </div>
                </div>

                <script>
                    window.onload = function() { 
                        setTimeout(function() { window.print(); }, 500);
                    }
                </script>
            </body>
            </html>
        `;

        const reportWindow = window.open('', '_blank', 'width=1000,height=800');
        reportWindow.document.write(reportHTML);
        reportWindow.document.close();
    };

    return (
        <div className="outcome-dashboard">

            <div className="dashboard-actions">
                <button
                    onClick={generatePrintableReport}
                    className="print-report-btn"
                >
                    <span className="btn-icon">🖨️</span> Print Report
                </button>
            </div>

            {/* State-Level Summary (If Selected) */}
            {selectedState && stateResults && (
                <div className="state-summary-section">
                    <h3 className="section-title">State Impact: {selectedState}</h3>
                    <div className="summary-cards state-cards">
                        <SummaryCard
                            title="Net Fiscal Gain"
                            value={`$${(stateResults.summary.totalStimulus / 1e9).toFixed(1)}B`}
                            subtitle={`Direct fiscal surplus over ${displayCount} years`}
                            color="var(--simulator-positive)"
                            info="Difference between federal program spending in-state and federal taxes collected from state residents."
                        />
                        <SummaryCard
                            title="Fiscal Benefit Ratio"
                            value={`${stateResults.summary.avgRatio.toFixed(2)}x`}
                            subtitle="Return on $1.00 paid"
                            color={stateResults.summary.avgRatio >= 1 ? 'var(--simulator-positive)' : 'var(--simulator-negative)'}
                        />
                        <SummaryCard
                            title={`${displayCount}Yr Direct Benefits`}
                            value={`$${(stateResults.summary.totalGet / 1e9).toFixed(1)}B`}
                            subtitle="Federal Program Outlays"
                            color="var(--simulator-positive)"
                        />
                        <SummaryCard
                            title={`${displayCount}Yr Federal Tax`}
                            value={`$${(stateResults.summary.totalPay / 1e9).toFixed(1)}B`}
                            subtitle="New Revenue from State"
                            color="var(--simulator-negative)"
                        />
                    </div>

                    {/* Persistent Definitions Legend */}
                    <div className="state-definitions">
                        <div className="def-item">
                            <span className="def-label"><span className="def-icon">💰</span> <span className="def-tag">Net Fiscal Gain</span></span>
                            The direct fiscal windfall to the state's economy. Calculated as (Federal Program Spending) minus (New Federal Taxes Paid).
                        </div>
                        <div className="def-item">
                            <span className="def-label"><span className="def-icon">⚖️</span> <span className="def-tag">Fiscal Benefit Ratio</span></span>
                            For every $1.00 paid in new federal taxes (Payroll, Income, Surtax), this is the value returned to the state in direct medical care funding.
                        </div>
                        <div className="def-item">
                            <span className="def-label"><span className="def-icon">📥</span> <span className="def-tag">{displayCount}Yr Direct Benefits</span></span>
                            Total value of federal payments for healthcare services provided to residents of the state.
                        </div>
                        <div className="def-item">
                            <span className="def-label"><span className="def-icon">📤</span> <span className="def-tag">{displayCount}Yr Federal Tax</span></span>
                            Total new federal taxes contributed by the state's residents and businesses to fund the SAFECARE system.
                        </div>
                    </div>
                </div>
            )}

            {/* Summary Cards (Pinned to Top) */}
            <div className="summary-cards">
                <SummaryCard
                    title={`${summary.totalYears}-Year Avg. Surplus`}
                    value={`$${Math.round(summary.averageSurplus / 1e9).toLocaleString()}B`}
                    subtitle={`Annual avg over ${summary.totalYears} years`}
                    color={summary.averageSurplus >= 0 ? 'var(--simulator-positive)' : 'var(--simulator-negative)'}
                />
                <SummaryCard
                    title="Total Savings vs Baseline Govt"
                    value={(() => {
                        const s = summary.totalSavings / 1e12;
                        const sign = s >= 0 ? '' : '-';
                        return `$${sign}${Math.abs(s).toFixed(1)}T`;
                    })()}
                    subtitle="Cumulative: CBO federal health outlays − SAFECare program"
                    color={summary.totalSavings >= 0 ? 'var(--simulator-positive)' : 'var(--simulator-negative)'}
                />
                <SummaryCard
                    title="Health Spending Reduction"
                    value={`${((summary.firstYear.baselineHealthShareGDP - summary.firstYear.safecareHealthShareGDP) / summary.firstYear.baselineHealthShareGDP * 100).toFixed(1)}%`}
                    subtitle="First year savings"
                    color="var(--simulator-positive)"
                />
                <SummaryCard
                    title="Household Savings"
                    value={`$${Math.round(householdSummary?.avgSavingsPerPerson || 0).toLocaleString()}`}
                    subtitle="Average per person/year"
                    color="var(--simulator-positive)"
                />
                <SummaryCard
                    title="GDP Impact"
                    value={`${finalGDPImpact > 0 ? '+' : ''}${finalGDPImpact}%`}
                    subtitle={`Estimated by ${yearlyResults[yearlyResults.length - 1].year}`}
                    color={parseFloat(finalGDPImpact) >= 0 ? 'var(--simulator-positive)' : 'var(--simulator-negative)'}
                />
            </div>

            <div className="dashboard-intro-text" role="note" aria-label="Important note on simulation dynamics">
                <p className="dashboard-intro-label">
                    <span className="dashboard-intro-icon" aria-hidden>⚠️</span>
                    Important: simulation dynamics
                </p>
                <p className="dashboard-intro-body">
                    Early years may show a negative trust‑fund balance as revenues ramp in more slowly than coverage; the system moves to strong surpluses once cost‑control provisions fully phase in (years 2–5).
                </p>
            </div>

            {/* Deficit Warning */}
            {hasDeficit && deficitAmount > 100e9 && (
                <div className="deficit-warning">
                    <h4>⚠️ Revenue Gap Detected</h4>
                    <p>
                        SAFECARE spending (${(firstYearSpending / 1e12).toFixed(2)}T) exceeds all revenue sources
                        (${(firstYearRevenue / 1e12).toFixed(2)}T contributions + ${(permittedGeneralFundTransfer / 1e12).toFixed(2)}T
                        permitted general fund) by <strong>${(deficitAmount / 1e12).toFixed(2)}T</strong> in year 1.
                    </p>

                    <p>
                        To close this gap, either:
                    </p>
                    <p>To close this gap, choose a strategy:</p>
                    <div className="gap-closure-grid">
                        {[
                            { id: 'balanced', label: 'Balanced', icon: '⚖️' },
                            { id: 'labor', label: 'Labor Mindful', icon: '👷' },
                            { id: 'infra', label: 'Infra Mindful', icon: '🏥' },
                            { id: 'tax', label: 'Tax Mindful', icon: '💸' },
                            { id: 'growth', label: 'Growth Opt', icon: '📈' }
                        ].map(s => {
                            const calcs = calculateGapClosure(s.id, deficitAmount, config);
                            if (!calcs) return null; // Should not happen if deficit > 0
                            return (
                                <button
                                    key={s.id}
                                    className={`gap-btn ${s.id}`}
                                    onClick={() => onUpdateConfig(calcs.changes)}
                                >
                                    <div className="gap-header">
                                        <span className="gap-icon">{s.icon}</span>
                                        <span className="gap-title">{s.label}</span>
                                    </div>
                                    <div className="gap-changes">
                                        {calcs.description.map((line, i) => (
                                            <div key={i} className="change-line">{line}</div>
                                        ))}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Revenue Neutral Message */}
            {!hasDeficit || deficitAmount < 100e9 && (
                <div className="revenue-neutral-message">
                    <h4>✅ Revenue Neutral</h4>
                    <p>
                        SAFECARE is revenue-neutral with current settings. Total revenues
                        (${(firstYearRevenue / 1e12).toFixed(2)}T contributions + ${(permittedGeneralFundTransfer / 1e12).toFixed(2)}T
                        general fund) cover spending of ${(firstYearSpending / 1e12).toFixed(2)}T.
                    </p>
                </div>
            )}

            {/* Success Gauge */}
            {results.successGauge && (
                <SuccessGauge successGauge={results.successGauge} />
            )}



            {/* Charts Grid */}
            <div className="charts-grid">
                {selectedState && stateResults && (
                    <div className="chart-row state-charts">
                        <StateStimulusChart
                            data={stateResults.yearlyResults}
                            stateName={selectedState}
                        />
                        <PayVsGetChart
                            data={stateResults.yearlyResults}
                            stateName={selectedState}
                        />
                    </div>
                )}

                <div className="chart-row">
                    <HealthSpendingChart
                        scenario={yearlyResults} // Sliced
                        baseline={baselineSliced} // Sliced
                        showEvents={showEventsLine}
                    />
                    <FederalDebtChart
                        data={displayResults}
                        scenario={yearlyResults} // Sliced
                        baseline={baselineSliced} // Sliced
                        showEvents={showEventsLine}
                    />
                </div>

                <div className="chart-row">
                    <HouseholdBurdenChart data={householdBurden} />
                    <DrugSpendingChart data={displayResults} />
                </div>

                <div className="chart-row">
                    <MedicalDebtChart data={displayResults} />
                    <MedSchoolDebtChart data={displayResults} />
                </div>

                {/* Macro Impact Chart */}
                <div className="chart-row single-col">
                    <MacroImpactChart data={displayMacro} />
                </div>

                {/* Program Cost & Funding Breakdown */}
                <section className="dashboard-section financials-overview">
                    <h3 className="section-title">
                        Program Cost & Funding Breakdown
                    </h3>

                    <div className="financial-grid">
                        <div className="chart-wrapper">
                            <CostFundingChart yearlyResults={yearlyResults} />
                        </div>

                        <div className="table-wrapper">
                            <h4 className="table-title">Detailed Annual Financials</h4>
                            <CostBreakdownTable yearlyResults={yearlyResults} config={config} />
                        </div>

                        <LtssInfoCard yearlyResults={yearlyResults} config={config} />
                    </div>
                </section>

                <DataExportPanel
                    yearlyResults={yearlyResults}
                    config={config}
                    rigorData={rigorData}
                    uncertaintyResult={uncertaintyResult}
                    />
                <DataSourcesDisclaimer />
            </div>
        </div>
    );
}

/**
 * Summary Card Component
 */
function SummaryCard({ title, value, subtitle, color, info }) {
    return (
        <div className="summary-card">
            <div className="card-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4>{title}</h4>
                {info && (
                    <div className="info-icon" title={info} style={{ cursor: 'help', fontSize: '12px', opacity: 0.7 }}>
                        ℹ️
                    </div>
                )}
            </div>
            <div className="card-value" style={{ color }}>{value}</div>
            <p className="card-subtitle">{subtitle}</p>
        </div>
    );
}
